import { z } from 'zod';

// Base wrapper for all exported data
export const ExportWrapperSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    generatedAt: z.string().datetime(),
    customerId: z.string(),
    apiVersion: z.string(),
    data: z.array(dataSchema),
  });

// Account
export const AccountSchema = z.object({
  customerId: z.string(),
  name: z.string(),
  currency: z.string(),
  timezone: z.string(),
  optimizationScore: z.number().nullable(),
  status: z.enum(['ENABLED', 'CANCELED', 'SUSPENDED', 'CLOSED']),
  autoAppliedRecommendations: z.array(z.string()),
  managerCustomerId: z.string().nullable(),
  testAccount: z.boolean(),
});

// Campaign
export const CampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum([
    'SEARCH',
    'DISPLAY',
    'SHOPPING',
    'VIDEO',
    'MULTI_CHANNEL',
    'LOCAL',
    'SMART',
    'PERFORMANCE_MAX',
    'LOCAL_SERVICES',
    'DISCOVERY',
    'TRAVEL',
    'DEMAND_GEN',
  ]),
  status: z.enum(['ENABLED', 'PAUSED', 'REMOVED']),
  budget: z.object({
    id: z.string(),
    name: z.string().nullable(),
    amountMicros: z.number(),
    deliveryMethod: z.enum(['STANDARD', 'ACCELERATED']).nullable(),
  }),
  biddingStrategy: z.object({
    type: z.string(),
    name: z.string().nullable(),
  }),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    ctr: z.number(),
    averageCpc: z.number(),
    averageCpm: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
    conversionRate: z.number(),
    costPerConversion: z.number().nullable(),
    conversionValue: z.number(),
    roas: z.number().nullable(),
    impressionShare: z.number().nullable(),
    searchLostIsRank: z.number().nullable(),
    searchLostIsBudget: z.number().nullable(),
    topImpressionPercentage: z.number().nullable(),
    absoluteTopImpressionPercentage: z.number().nullable(),
  }),
});

// Asset Group (Performance Max)
export const AssetGroupSchema = z.object({
  id: z.string(),
  campaignId: z.string(),
  name: z.string(),
  status: z.enum(['ENABLED', 'PAUSED', 'REMOVED']),
  adStrength: z.enum(['UNSPECIFIED', 'UNKNOWN', 'PENDING', 'NO_ADS', 'POOR', 'AVERAGE', 'GOOD', 'EXCELLENT']).nullable(),
  finalUrl: z.string(),
  finalMobileUrl: z.string().nullable(),
  path1: z.string().nullable(),
  path2: z.string().nullable(),
  assets: z.object({
    headlines: z.array(z.string()),
    longHeadlines: z.array(z.string()),
    descriptions: z.array(z.string()),
    businessName: z.string().nullable(),
    callToActions: z.array(z.string()),
  }),
});

// Asset
export const AssetSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  type: z.enum([
    'TEXT',
    'IMAGE',
    'MEDIA_BUNDLE',
    'YOUTUBE_VIDEO',
    'LEAD_FORM',
    'BOOK_ON_GOOGLE',
    'PROMOTION',
    'CALLOUT',
    'STRUCTURED_SNIPPET',
    'SITELINK',
    'PAGE_FEED',
    'DYNAMIC_EDUCATION',
    'MOBILE_APP',
    'HOTEL_CALLOUT',
    'CALL',
    'PRICE',
    'CALL_TO_ACTION',
    'DYNAMIC_REAL_ESTATE',
    'DYNAMIC_CUSTOM',
    'DYNAMIC_HOTELS_AND_RENTALS',
    'DYNAMIC_FLIGHTS',
    'DISCOVERY_CAROUSEL_CARD',
    'DYNAMIC_TRAVEL',
    'DYNAMIC_LOCAL',
    'DYNAMIC_JOBS',
    'LOCATION',
    'HOTEL_PROPERTY',
  ]),
  status: z.enum(['ENABLED', 'REMOVED', 'ARCHIVED']).nullable(),
  performanceLabel: z.enum(['PENDING', 'LEARNING', 'LOW', 'GOOD', 'BEST', 'UNKNOWN']).nullable(),
  textContent: z.string().nullable(),
  imageUrl: z.string().nullable(),
  imageFileSize: z.number().nullable(),
  imageMimeType: z.string().nullable(),
  imageWidth: z.number().nullable(),
  imageHeight: z.number().nullable(),
  youtubeVideoId: z.string().nullable(),
  youtubeVideoTitle: z.string().nullable(),
});

// Ad
export const AdSchema = z.object({
  id: z.string(),
  adGroupId: z.string(),
  adGroupName: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  type: z.enum([
    'TEXT_AD',
    'EXPANDED_TEXT_AD',
    'RESPONSIVE_SEARCH_AD',
    'RESPONSIVE_DISPLAY_AD',
    'IMAGE_AD',
    'VIDEO_AD',
    'SHOPPING_PRODUCT_AD',
    'SHOPPING_SMART_AD',
    'APP_AD',
    'APP_ENGAGEMENT_AD',
    'CALL_AD',
    'DISCOVERY_MULTI_ASSET_AD',
    'DISCOVERY_CAROUSEL_AD',
    'DISCOVERY_VIDEO_RESPONSIVE_AD',
    'SMART_CAMPAIGN_AD',
    'VIDEO_RESPONSIVE_AD',
    'DEMAND_GEN_MULTI_ASSET_AD',
    'DEMAND_GEN_CAROUSEL_AD',
    'DEMAND_GEN_VIDEO_RESPONSIVE_AD',
    'DEMAND_GEN_PRODUCT_AD',
  ]),
  status: z.enum(['ENABLED', 'PAUSED', 'REMOVED']),
  headlines: z.array(z.object({
    text: z.string(),
    pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).nullable(),
  })),
  descriptions: z.array(z.object({
    text: z.string(),
    pinnedField: z.enum(['DESCRIPTION_1', 'DESCRIPTION_2']).nullable(),
  })),
  finalUrls: z.array(z.string()),
  finalMobileUrls: z.array(z.string()),
  path1: z.string().nullable(),
  path2: z.string().nullable(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    ctr: z.number(),
    averageCpc: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
    conversionRate: z.number(),
  }),
});

// Keyword
export const KeywordSchema = z.object({
  id: z.string(),
  adGroupId: z.string(),
  adGroupName: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  text: z.string(),
  matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
  status: z.enum(['ENABLED', 'PAUSED', 'REMOVED']),
  approvalStatus: z.enum(['APPROVED', 'APPROVED_LIMITED', 'DISAPPROVED', 'UNDER_REVIEW']).nullable(),
  qualityScore: z.number().nullable(),
  expectedCtr: z.enum(['BELOW_AVERAGE', 'AVERAGE', 'ABOVE_AVERAGE']).nullable(),
  adRelevance: z.enum(['BELOW_AVERAGE', 'AVERAGE', 'ABOVE_AVERAGE']).nullable(),
  landingPageExperience: z.enum(['BELOW_AVERAGE', 'AVERAGE', 'ABOVE_AVERAGE']).nullable(),
  cpcBidMicros: z.number().nullable(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    ctr: z.number(),
    averageCpc: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
    conversionRate: z.number(),
  }),
});

// Negative Keyword
export const NegativeKeywordSchema = z.object({
  id: z.string(),
  text: z.string(),
  matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
  level: z.enum(['CAMPAIGN', 'AD_GROUP', 'SHARED_SET']),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  adGroupId: z.string().nullable(),
  adGroupName: z.string().nullable(),
  sharedSetId: z.string().nullable(),
  sharedSetName: z.string().nullable(),
});

// Search Term
export const SearchTermSchema = z.object({
  searchTerm: z.string(),
  adGroupId: z.string(),
  adGroupName: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  keywordId: z.string().nullable(),
  keywordText: z.string().nullable(),
  status: z.enum(['ADDED', 'EXCLUDED', 'ADDED_EXCLUDED', 'NONE']),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    ctr: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
    conversionRate: z.number(),
  }),
});

// Location Target
export const LocationTargetSchema = z.object({
  id: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  locationId: z.string(),
  locationName: z.string(),
  locationType: z.enum(['COUNTRY', 'REGION', 'CITY', 'POSTAL_CODE', 'RADIUS']),
  targetType: z.enum(['TARGET', 'EXCLUDE']),
  bidModifier: z.number().nullable(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
  }),
});

// Audience
export const AudienceSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum([
    'AFFINITY',
    'IN_MARKET',
    'CUSTOM_INTENT',
    'CUSTOM_AFFINITY',
    'REMARKETING',
    'SIMILAR',
    'COMBINED',
    'DETAILED_DEMOGRAPHICS',
    'LIFE_EVENTS',
  ]),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  adGroupId: z.string().nullable(),
  adGroupName: z.string().nullable(),
  targetType: z.enum(['TARGET', 'OBSERVE', 'EXCLUDE']),
  bidModifier: z.number().nullable(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
  }),
});

// Recommendation
export const RecommendationSchema = z.object({
  id: z.string(),
  type: z.string(),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  adGroupId: z.string().nullable(),
  adGroupName: z.string().nullable(),
  impact: z.object({
    potentialBudgetMicros: z.number().nullable(),
    potentialClicks: z.number().nullable(),
    potentialConversions: z.number().nullable(),
    potentialCostMicros: z.number().nullable(),
    potentialImpressions: z.number().nullable(),
  }).nullable(),
  dismissed: z.boolean(),
});

// Conversion Action
export const ConversionActionSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.enum([
    'DEFAULT',
    'PAGE_VIEW',
    'PURCHASE',
    'SIGNUP',
    'LEAD',
    'DOWNLOAD',
    'ADD_TO_CART',
    'BEGIN_CHECKOUT',
    'SUBSCRIBE_PAID',
    'PHONE_CALL_LEAD',
    'IMPORTED_LEAD',
    'SUBMIT_LEAD_FORM',
    'BOOK_APPOINTMENT',
    'REQUEST_QUOTE',
    'GET_DIRECTIONS',
    'OUTBOUND_CLICK',
    'CONTACT',
    'ENGAGEMENT',
    'STORE_VISIT',
    'STORE_SALE',
    'QUALIFIED_LEAD',
    'CONVERTED_LEAD',
  ]),
  status: z.enum(['ENABLED', 'REMOVED', 'HIDDEN']),
  type: z.enum([
    'AD_CALL',
    'CLICK_TO_CALL',
    'GOOGLE_PLAY_DOWNLOAD',
    'GOOGLE_PLAY_IN_APP_PURCHASE',
    'UPLOAD_CALLS',
    'UPLOAD_CLICKS',
    'WEBPAGE',
    'WEBSITE_CALL',
    'STORE_SALES_DIRECT_UPLOAD',
    'STORE_SALES',
    'FIREBASE_ANDROID_FIRST_OPEN',
    'FIREBASE_ANDROID_IN_APP_PURCHASE',
    'FIREBASE_ANDROID_CUSTOM',
    'FIREBASE_IOS_FIRST_OPEN',
    'FIREBASE_IOS_IN_APP_PURCHASE',
    'FIREBASE_IOS_CUSTOM',
    'THIRD_PARTY_APP_ANALYTICS_ANDROID_FIRST_OPEN',
    'THIRD_PARTY_APP_ANALYTICS_ANDROID_IN_APP_PURCHASE',
    'THIRD_PARTY_APP_ANALYTICS_ANDROID_CUSTOM',
    'THIRD_PARTY_APP_ANALYTICS_IOS_FIRST_OPEN',
    'THIRD_PARTY_APP_ANALYTICS_IOS_IN_APP_PURCHASE',
    'THIRD_PARTY_APP_ANALYTICS_IOS_CUSTOM',
    'ANDROID_APP_PRE_REGISTRATION',
    'ANDROID_INSTALLS_ALL_OTHER_APPS',
    'FLOODLIGHT_ACTION',
    'FLOODLIGHT_TRANSACTION',
    'GOOGLE_HOSTED',
    'LEAD_FORM_SUBMIT',
    'SALESFORCE',
    'SEARCH_ADS_360',
    'SMART_CAMPAIGN_AD_CLICKS_TO_CALL',
    'SMART_CAMPAIGN_MAP_CLICKS_TO_CALL',
    'SMART_CAMPAIGN_MAP_DIRECTIONS',
    'SMART_CAMPAIGN_TRACKED_CALLS',
    'STORE_VISITS',
    'WEBPAGE_CODELESS',
    'UNIVERSAL_ANALYTICS_GOAL',
    'UNIVERSAL_ANALYTICS_TRANSACTION',
    'GOOGLE_ANALYTICS_4_CUSTOM',
    'GOOGLE_ANALYTICS_4_PURCHASE',
  ]),
  countingType: z.enum(['ONE_PER_CLICK', 'MANY_PER_CLICK']),
  attributionModel: z.string(),
  conversionWindow: z.number(),
  viewThroughLookbackWindow: z.number().nullable(),
  includeInConversionsMetric: z.boolean(),
  valueSetting: z.object({
    defaultValue: z.number().nullable(),
    alwaysUseDefaultValue: z.boolean(),
  }).nullable(),
});

// Conversion Stats
export const ConversionStatsSchema = z.object({
  conversionActionId: z.string(),
  conversionActionName: z.string(),
  conversions: z.number(),
  conversionValue: z.number(),
  allConversions: z.number(),
  allConversionValue: z.number(),
  viewThroughConversions: z.number(),
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
});

// Budget
export const BudgetSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  amountMicros: z.number(),
  deliveryMethod: z.enum(['STANDARD', 'ACCELERATED']).nullable(),
  period: z.enum(['DAILY', 'CUSTOM_PERIOD']),
  totalAmountMicros: z.number().nullable(),
  status: z.enum(['ENABLED', 'REMOVED']),
  type: z.enum(['STANDARD', 'FIXED_CPA', 'SMART_CAMPAIGN', 'LOCAL_SERVICES']),
  associatedCampaigns: z.array(z.object({
    id: z.string(),
    name: z.string(),
  })),
  recommendedBudgetAmountMicros: z.number().nullable(),
  recommendedBudgetEstimatedChangeWeeklyClicks: z.number().nullable(),
  recommendedBudgetEstimatedChangeWeeklyCost: z.number().nullable(),
});

// Bidding Strategy
export const BiddingStrategySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum([
    'COMMISSION',
    'ENHANCED_CPC',
    'MANUAL_CPC',
    'MANUAL_CPM',
    'MANUAL_CPV',
    'MAXIMIZE_CONVERSIONS',
    'MAXIMIZE_CONVERSION_VALUE',
    'PAGE_ONE_PROMOTED',
    'PERCENT_CPC',
    'TARGET_CPA',
    'TARGET_CPM',
    'TARGET_IMPRESSION_SHARE',
    'TARGET_OUTRANK_SHARE',
    'TARGET_ROAS',
    'TARGET_SPEND',
  ]),
  status: z.enum(['ENABLED', 'REMOVED']),
  targetCpaMicros: z.number().nullable(),
  targetRoas: z.number().nullable(),
  targetSpendMicros: z.number().nullable(),
  targetImpressionShare: z.number().nullable(),
  campaignCount: z.number(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
  }),
});

// Extension (Ad Extension / Asset)
export const ExtensionSchema = z.object({
  id: z.string(),
  type: z.enum([
    'SITELINK',
    'CALLOUT',
    'CALL',
    'STRUCTURED_SNIPPET',
    'LOCATION',
    'AFFILIATE_LOCATION',
    'PRICE',
    'PROMOTION',
    'APP',
    'LEAD_FORM',
    'IMAGE',
  ]),
  status: z.enum(['ENABLED', 'REMOVED']),
  level: z.enum(['CUSTOMER', 'CAMPAIGN', 'AD_GROUP']),
  campaignId: z.string().nullable(),
  adGroupId: z.string().nullable(),
  content: z.record(z.string(), z.unknown()),
  performanceLabel: z.enum(['PENDING', 'LEARNING', 'LOW', 'GOOD', 'BEST', 'UNKNOWN']).nullable(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    costMicros: z.number(),
  }),
});

// Landing Page
export const LandingPageSchema = z.object({
  url: z.string(),
  expandedFinalUrl: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  adGroupId: z.string().nullable(),
  adGroupName: z.string().nullable(),
  mobileSpeedScore: z.number().nullable(),
  mobileFriendlyClickRate: z.number().nullable(),
  validAcceleratedMobilePagesClickRate: z.number().nullable(),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
  }),
});

// Performance (time-based metrics)
export const PerformanceSchema = z.object({
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
    period: z.enum(['LAST_7_DAYS', 'LAST_30_DAYS', 'LAST_90_DAYS', 'LAST_365_DAYS', 'CUSTOM']),
  }),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    ctr: z.number(),
    averageCpc: z.number(),
    averageCpm: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
    conversionRate: z.number(),
    costPerConversion: z.number().nullable(),
    conversionValue: z.number(),
    roas: z.number().nullable(),
    impressionShare: z.number().nullable(),
  }),
  dailyBreakdown: z.array(z.object({
    date: z.string(),
    impressions: z.number(),
    clicks: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
  })),
});

// Quality Signals
export const QualitySignalsSchema = z.object({
  campaignId: z.string(),
  campaignName: z.string(),
  adGroupId: z.string().nullable(),
  adGroupName: z.string().nullable(),
  averageQualityScore: z.number().nullable(),
  qualityScoreDistribution: z.object({
    belowAverage: z.number(),
    average: z.number(),
    aboveAverage: z.number(),
  }).nullable(),
  adStrengthDistribution: z.object({
    poor: z.number(),
    average: z.number(),
    good: z.number(),
    excellent: z.number(),
  }).nullable(),
  landingPageExperienceDistribution: z.object({
    belowAverage: z.number(),
    average: z.number(),
    aboveAverage: z.number(),
  }).nullable(),
});

// Label
export const LabelSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  textColor: z.string().nullable(),
  backgroundColor: z.string().nullable(),
  resourceType: z.enum(['CAMPAIGN', 'AD_GROUP', 'AD', 'KEYWORD', 'CUSTOMER']),
  resourceId: z.string(),
  resourceName: z.string(),
});

// Shopping (Merchant Center)
export const ShoppingSchema = z.object({
  merchantId: z.string(),
  productId: z.string(),
  offerId: z.string(),
  title: z.string(),
  brand: z.string().nullable(),
  categoryL1: z.string().nullable(),
  categoryL2: z.string().nullable(),
  categoryL3: z.string().nullable(),
  customLabel0: z.string().nullable(),
  customLabel1: z.string().nullable(),
  customLabel2: z.string().nullable(),
  customLabel3: z.string().nullable(),
  customLabel4: z.string().nullable(),
  productTypeL1: z.string().nullable(),
  productTypeL2: z.string().nullable(),
  productTypeL3: z.string().nullable(),
  channel: z.enum(['ONLINE', 'LOCAL']),
  status: z.enum(['NOT_ELIGIBLE', 'ELIGIBLE_LIMITED', 'ELIGIBLE']),
  issues: z.array(z.string()),
  metrics: z.object({
    impressions: z.number(),
    clicks: z.number(),
    costMicros: z.number(),
    conversions: z.number(),
    conversionValue: z.number(),
  }),
});

// Change History
export const ChangeHistorySchema = z.object({
  changeDateTime: z.string(),
  resourceType: z.string(),
  resourceName: z.string(),
  changeType: z.enum(['CREATE', 'UPDATE', 'REMOVE']),
  clientType: z.enum([
    'GOOGLE_ADS_WEB_CLIENT',
    'GOOGLE_ADS_AUTOMATED_RULE',
    'GOOGLE_ADS_SCRIPTS',
    'GOOGLE_ADS_BULK_UPLOAD',
    'GOOGLE_ADS_API',
    'GOOGLE_ADS_EDITOR',
    'GOOGLE_ADS_MOBILE_APP',
    'GOOGLE_ADS_RECOMMENDATIONS',
    'SEARCH_ADS_360_SYNC',
    'SEARCH_ADS_360_POST',
    'INTERNAL_TOOL',
    'OTHER',
  ]),
  userEmail: z.string().nullable(),
  oldValue: z.record(z.string(), z.unknown()).nullable(),
  newValue: z.record(z.string(), z.unknown()).nullable(),
});

// Audit Summary
export const AuditSummarySchema = z.object({
  exportedAt: z.string().datetime(),
  customerId: z.string(),
  accountName: z.string(),
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
  totals: z.object({
    campaigns: z.number(),
    activeCampaigns: z.number(),
    adGroups: z.number(),
    ads: z.number(),
    keywords: z.number(),
    assetGroups: z.number(),
    totalSpendMicros: z.number(),
    totalConversions: z.number(),
    totalConversionValue: z.number(),
  }),
  healthIndicators: z.object({
    optimizationScore: z.number().nullable(),
    averageQualityScore: z.number().nullable(),
    disapprovedAdsCount: z.number(),
    limitedAdsCount: z.number(),
    lowQualityKeywordsCount: z.number(),
    recommendationsCount: z.number(),
    budgetLimitedCampaignsCount: z.number(),
  }),
  exportedFiles: z.array(z.string()),
});

// Headlines (extracted from ads)
export const HeadlineSchema = z.object({
  text: z.string(),
  adId: z.string(),
  adGroupId: z.string(),
  adGroupName: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  pinnedPosition: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).nullable(),
  performanceLabel: z.enum(['PENDING', 'LEARNING', 'LOW', 'GOOD', 'BEST', 'UNKNOWN']).nullable(),
});

// Descriptions (extracted from ads)
export const DescriptionSchema = z.object({
  text: z.string(),
  adId: z.string(),
  adGroupId: z.string(),
  adGroupName: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  pinnedPosition: z.enum(['DESCRIPTION_1', 'DESCRIPTION_2']).nullable(),
  performanceLabel: z.enum(['PENDING', 'LEARNING', 'LOW', 'GOOD', 'BEST', 'UNKNOWN']).nullable(),
});

// Image Asset
export const ImageAssetSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  url: z.string(),
  fileSize: z.number().nullable(),
  mimeType: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  performanceLabel: z.enum(['PENDING', 'LEARNING', 'LOW', 'GOOD', 'BEST', 'UNKNOWN']).nullable(),
  usedIn: z.array(z.object({
    type: z.enum(['CAMPAIGN', 'AD_GROUP', 'AD', 'ASSET_GROUP']),
    id: z.string(),
    name: z.string(),
  })),
});

// Video Asset
export const VideoAssetSchema = z.object({
  id: z.string(),
  youtubeVideoId: z.string(),
  youtubeVideoTitle: z.string().nullable(),
  durationMillis: z.number().nullable(),
  performanceLabel: z.enum(['PENDING', 'LEARNING', 'LOW', 'GOOD', 'BEST', 'UNKNOWN']).nullable(),
  usedIn: z.array(z.object({
    type: z.enum(['CAMPAIGN', 'AD_GROUP', 'AD', 'ASSET_GROUP']),
    id: z.string(),
    name: z.string(),
  })),
});

// Recommendation Impact
export const RecommendationImpactSchema = z.object({
  recommendationId: z.string(),
  type: z.string(),
  impactMetrics: z.object({
    potentialBudgetMicros: z.number().nullable(),
    potentialClicks: z.number().nullable(),
    potentialConversions: z.number().nullable(),
    potentialCostMicros: z.number().nullable(),
    potentialImpressions: z.number().nullable(),
    potentialViewsCount: z.number().nullable(),
    potentialVideoViews: z.number().nullable(),
  }),
  baseMetrics: z.object({
    currentBudgetMicros: z.number().nullable(),
    currentClicks: z.number().nullable(),
    currentConversions: z.number().nullable(),
    currentCostMicros: z.number().nullable(),
    currentImpressions: z.number().nullable(),
  }).nullable(),
});

// OAuth Connection Status
export const GoogleAdsConnectionSchema = z.object({
  connected: z.boolean(),
  customerIds: z.array(z.object({
    customerId: z.string(),
    name: z.string(),
    isManager: z.boolean(),
    canManageClients: z.boolean(),
  })),
  expiresAt: z.string().datetime().nullable(),
  scopes: z.array(z.string()),
});

// Export Progress
export const ExportProgressSchema = z.object({
  jobId: z.string(),
  status: z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED']),
  customerId: z.string(),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  progress: z.number().min(0).max(100),
  currentStep: z.string().nullable(),
  files: z.array(z.object({
    name: z.string(),
    status: z.enum(['PENDING', 'COMPLETED', 'FAILED']),
    recordCount: z.number().nullable(),
    error: z.string().nullable(),
  })),
  error: z.string().nullable(),
});
